import * as THREE from 'three';
import type { GltfAssetLoader, LoaderManifest, TextureAssetLoader } from './assets';
import {
  CAPE_ID_TO_ASSET_ID,
  EYE_ID_TO_ASSET_ID,
  HEAD_ID_TO_ASSET_ID,
  characterAnimationClipsContainAlias,
  createEyeTextureStore,
  EYE_EXPRESSIONS,
  type CapeId,
  type CharacterCustomization,
  type CharacterAnimationAlias,
  type EyeExpression,
  type EyeId,
  type HeadId,
} from './renderer/character';
import type { EyeTextureStore } from './renderer/character';
import {
  createProductionAdapter,
  type CharacterPreviewAdapter,
} from './ui/screens/character-preview-adapter';

export const BLOB_ANIMATION_BUNDLE_ASSET_IDS = {
  locomotion: 'character-animation-blob-locomotion',
  actions: 'character-animation-blob-actions',
  boardActions: 'character-animation-blob-board-actions',
  emotes: 'character-animation-blob-emotes',
} as const;

export type BlobAnimationBundle = keyof typeof BLOB_ANIMATION_BUNDLE_ASSET_IDS;
export type CharacterAnimationBundlePolicy = 'required' | 'degraded' | 'debug-delayed';
export type CharacterAnimationBundleStatus =
  | 'not-requested'
  | 'pending'
  | 'loaded'
  | 'failed'
  | 'omitted';

export interface CharacterAnimationBundlePolicyOptions {
  readonly policy: CharacterAnimationBundlePolicy;
  readonly requiredAliases?: readonly CharacterAnimationAlias[];
  readonly delayMs?: number;
  readonly omitBundles?: readonly BlobAnimationBundle[];
  readonly reason?: string;
}

export interface CharacterAdapterPreloadHandle {
  readonly adapter: CharacterPreviewAdapter;
  readonly eyeTextureStore: EyeTextureStore;
  readonly assetCounts: CharacterAdapterAssetCounts;
  areAllCosmeticsLoaded(): boolean;
  ensureAllCosmeticsLoaded(): Promise<void>;
  loadAnimationBundle(bundle: BlobAnimationBundle): Promise<THREE.AnimationClip[]>;
  addAnimationBundleToAdapter(bundle: BlobAnimationBundle): Promise<void>;
  ensureAnimationBundles(
    bundles: readonly BlobAnimationBundle[],
    options: CharacterAnimationBundlePolicyOptions,
  ): Promise<void>;
  getAnimationBundleStatus(bundle: BlobAnimationBundle): CharacterAnimationBundleStatus;
}

export interface CharacterAdapterAssetCounts {
  heads: number;
  eyes: number;
  capes: number;
  locomotionClips: number;
}

interface CharacterAdapterPreloadArgs {
  readonly manifest: LoaderManifest;
  readonly gltfLoader: GltfAssetLoader;
  readonly textureLoader: TextureAssetLoader;
  readonly assetUrl?: (path: string) => string;
  readonly initialCustomization?: CharacterCustomization | null;
  readonly preloadAllCosmetics?: boolean;
  readonly warn?: (...args: unknown[]) => void;
}

const BASE_ANIMATION_BUNDLES: readonly BlobAnimationBundle[] = ['locomotion'];

export async function createCharacterAdapterPreloadHandle(
  args: CharacterAdapterPreloadArgs,
): Promise<CharacterAdapterPreloadHandle> {
  const assetUrl = args.assetUrl ?? ((path: string) => path);
  const warn = args.warn ?? ((...parts: unknown[]) => console.warn(...parts));
  const preloadAllCosmetics = args.preloadAllCosmetics ?? true;
  const initialCustomization = args.initialCustomization ?? null;
  const allHeadIds = Object.keys(HEAD_ID_TO_ASSET_ID) as HeadId[];
  const allEyeIds = Object.keys(EYE_ID_TO_ASSET_ID) as EyeId[];
  const allCapeIds = Object.keys(CAPE_ID_TO_ASSET_ID) as Exclude<CapeId, 'none'>[];
  const loadedAnimationClips: THREE.AnimationClip[] = [];
  const animationBundlePromises = new Map<BlobAnimationBundle, Promise<THREE.AnimationClip[]>>();
  const addedAnimationBundlePromises = new Map<BlobAnimationBundle, Promise<void>>();
  const addedBundles = new Set<BlobAnimationBundle>();
  const bundleStatus = new Map<BlobAnimationBundle, CharacterAnimationBundleStatus>();
  const loadedEyeExpressions = new Set<string>();
  let allCosmeticsLoadPromise: Promise<void> | null = null;

  const loadAnimationBundle = async (
    bundle: BlobAnimationBundle,
  ): Promise<THREE.AnimationClip[]> => {
    const cached = animationBundlePromises.get(bundle);
    if (cached != null) return cached;

    const assetId = BLOB_ANIMATION_BUNDLE_ASSET_IDS[bundle];
    const entry = args.manifest.byId.get(assetId);
    if (entry == null) {
      warn(`[smashparty] manifest missing character animation bundle: ${assetId}`);
      bundleStatus.set(bundle, 'failed');
      return [];
    }

    bundleStatus.set(bundle, 'pending');
    const promise = args.gltfLoader
      .loadAsset(assetUrl('/' + entry.path))
      .then((asset) => {
        bundleStatus.set(bundle, 'loaded');
        return asset.animations;
      })
      .catch((error: unknown) => {
        warn(`[smashparty] character animation bundle load failed: ${assetId}`, error);
        bundleStatus.set(bundle, 'failed');
        return [];
      });
    animationBundlePromises.set(bundle, promise);
    return promise;
  };

  const bodyEntry = args.manifest.byId.get('character-body');
  if (bodyEntry == null) throw new Error('manifest missing character-body');
  const bodyAsset = await args.gltfLoader.loadAsset(assetUrl('/' + bodyEntry.path));
  loadedAnimationClips.push(...bodyAsset.animations);
  const baseAnimationClips = [...bodyAsset.animations];

  for (const bundle of BASE_ANIMATION_BUNDLES) {
    const clips = await loadAnimationBundle(bundle);
    baseAnimationClips.push(...clips);
    loadedAnimationClips.push(...clips);
    addedBundles.add(bundle);
  }

  const headGltfScenes: Partial<Record<HeadId, THREE.Group>> = {};
  const manifestEntries: Partial<Record<HeadId, { placeholder: boolean }>> = {};
  const headLoadPromises = new Map<HeadId, Promise<void>>();
  const loadHead = (headId: HeadId): Promise<void> => {
    if (headGltfScenes[headId] != null) return Promise.resolve();
    const cached = headLoadPromises.get(headId);
    if (cached != null) return cached;
    const assetId = HEAD_ID_TO_ASSET_ID[headId];
    const entry = args.manifest.byId.get(assetId);
    if (entry == null) {
      warn(`[smashparty] manifest missing head asset: ${assetId}`);
      return Promise.resolve();
    }
    manifestEntries[headId] = { placeholder: entry.placeholder ?? false };
    const promise = args.gltfLoader
      .load(assetUrl('/' + entry.path))
      .then((scene) => {
        headGltfScenes[headId] = scene;
      })
      .catch((error: unknown) => {
        warn(`[smashparty] head GLB load failed: ${assetId}`, error);
      })
      .finally(() => {
        headLoadPromises.delete(headId);
      });
    headLoadPromises.set(headId, promise);
    return promise;
  };

  const eyeTextures: Partial<Record<EyeId, THREE.Texture>> = {};
  const eyeLoadPromises = new Map<EyeId, Promise<void>>();
  const loadEye = (eyeId: EyeId): Promise<void> => {
    if (eyeTextures[eyeId] != null) return Promise.resolve();
    const cached = eyeLoadPromises.get(eyeId);
    if (cached != null) return cached;
    const assetId = EYE_ID_TO_ASSET_ID[eyeId];
    const entry = args.manifest.byId.get(assetId);
    if (entry == null) {
      warn(`[smashparty] manifest missing eye asset: ${assetId}`);
      return Promise.resolve();
    }
    const promise = args.textureLoader
      .load(assetUrl('/' + entry.path))
      .then((texture) => {
        eyeTextures[eyeId] = texture;
      })
      .catch((error: unknown) => {
        warn(`[smashparty] eye texture load failed: ${assetId}`, error);
      })
      .finally(() => {
        eyeLoadPromises.delete(eyeId);
      });
    eyeLoadPromises.set(eyeId, promise);
    return promise;
  };

  const capeTextures: Partial<Record<Exclude<CapeId, 'none'>, THREE.Texture>> = {};
  const capeLoadPromises = new Map<Exclude<CapeId, 'none'>, Promise<void>>();
  const loadCape = (capeId: Exclude<CapeId, 'none'>): Promise<void> => {
    if (capeTextures[capeId] != null) return Promise.resolve();
    const cached = capeLoadPromises.get(capeId);
    if (cached != null) return cached;
    const assetId = CAPE_ID_TO_ASSET_ID[capeId];
    const entry = args.manifest.byId.get(assetId);
    if (entry == null) {
      warn(`[smashparty] manifest missing cape asset: ${assetId}`);
      return Promise.resolve();
    }
    const promise = args.textureLoader
      .load(assetUrl('/' + entry.path))
      .then((texture) => {
        capeTextures[capeId] = texture;
      })
      .catch((error: unknown) => {
        warn(`[smashparty] cape texture load failed: ${assetId}`, error);
      })
      .finally(() => {
        capeLoadPromises.delete(capeId);
      });
    capeLoadPromises.set(capeId, promise);
    return promise;
  };

  const assetCounts: CharacterAdapterAssetCounts = {
    heads: 0,
    eyes: 0,
    capes: 0,
    locomotionClips: baseAnimationClips.length - bodyAsset.animations.length,
  };
  const refreshAssetCounts = (): void => {
    assetCounts.heads = Object.keys(headGltfScenes).length;
    assetCounts.eyes = Object.keys(eyeTextures).length;
    assetCounts.capes = Object.keys(capeTextures).length;
  };

  const baseHeadIds = preloadAllCosmetics
    ? allHeadIds
    : Array.from(new Set<HeadId>(['horse', initialCustomization?.head ?? 'horse']));
  const baseEyeIds = preloadAllCosmetics
    ? allEyeIds
    : Array.from(new Set<EyeId>(['dots', initialCustomization?.eyes ?? 'dots']));
  const baseCapeIds =
    preloadAllCosmetics ||
    initialCustomization?.cape == null ||
    initialCustomization.cape === 'none'
      ? preloadAllCosmetics
        ? allCapeIds
        : []
      : [initialCustomization.cape];

  await Promise.all([
    ...baseHeadIds.map((headId) => loadHead(headId)),
    ...baseEyeIds.map((eyeId) => loadEye(eyeId)),
    ...baseCapeIds.map((capeId) => loadCape(capeId)),
  ]);
  refreshAssetCounts();

  const areAllCosmeticsLoaded = (): boolean =>
    allHeadIds.every((headId) => headGltfScenes[headId] != null) &&
    allEyeIds.every((eyeId) => eyeTextures[eyeId] != null) &&
    allCapeIds.every((capeId) => capeTextures[capeId] != null) &&
    allEyeIds.every((eyeId) =>
      EYE_EXPRESSIONS.every((expression) =>
        expression === 'base' ? true : loadedEyeExpressions.has(`${eyeId}:${expression}`),
      ),
    );

  const eyeTextureStore = createEyeTextureStore({
    manifest: args.manifest,
    textureLoader: args.textureLoader,
    baseTextures: eyeTextures,
    maxCachedPixels: 20_000_000,
  });

  const loadEyeExpression = async (eyeId: EyeId, expression: EyeExpression): Promise<void> => {
    if (expression === 'base') return;
    const texture = await eyeTextureStore.resolve(eyeId, expression);
    if (texture != null) loadedEyeExpressions.add(`${eyeId}:${expression}`);
  };

  const ensureAllCosmeticsLoaded = (): Promise<void> => {
    if (areAllCosmeticsLoaded()) return Promise.resolve();
    if (allCosmeticsLoadPromise != null) return allCosmeticsLoadPromise;
    allCosmeticsLoadPromise = Promise.all([
      ...allHeadIds.map((headId) => loadHead(headId)),
      ...allEyeIds.map((eyeId) => loadEye(eyeId)),
      ...allCapeIds.map((capeId) => loadCape(capeId)),
      ...allEyeIds.flatMap((eyeId) =>
        EYE_EXPRESSIONS.map((expression) => loadEyeExpression(eyeId, expression)),
      ),
    ])
      .then(() => {
        refreshAssetCounts();
      })
      .finally(() => {
        allCosmeticsLoadPromise = null;
      });
    return allCosmeticsLoadPromise;
  };

  const adapter = createProductionAdapter({
    bodyGltfScene: bodyAsset.scene,
    bodyAnimations: baseAnimationClips,
    headGltfScenes,
    fallbackHeadGltfScene: headGltfScenes.horse ?? bodyAsset.scene,
    manifestEntries,
    eyeTextures,
    eyeTextureStore,
    capeTextures,
  });

  const addAnimationBundleToAdapter = (bundle: BlobAnimationBundle): Promise<void> => {
    const cached = addedAnimationBundlePromises.get(bundle);
    if (cached != null) return cached;
    const promise = loadAnimationBundle(bundle).then((clips) => {
      if (!addedBundles.has(bundle)) {
        adapter.addAnimationClips?.(clips);
        loadedAnimationClips.push(...clips);
        addedBundles.add(bundle);
      }
    });
    addedAnimationBundlePromises.set(bundle, promise);
    return promise;
  };

  const ensureAnimationBundles = async (
    bundles: readonly BlobAnimationBundle[],
    options: CharacterAnimationBundlePolicyOptions,
  ): Promise<void> => {
    const omitBundles = new Set(options.omitBundles ?? []);
    const delayMs = Math.max(0, options.delayMs ?? 0);
    if (options.policy === 'debug-delayed' && delayMs > 0) {
      await delay(delayMs);
    }

    for (const bundle of bundles) {
      if (options.policy === 'debug-delayed' && omitBundles.has(bundle)) {
        bundleStatus.set(bundle, 'omitted');
        warn(
          `[smashparty] character animation bundle intentionally omitted: ${bundle}`,
          options.reason ?? '',
        );
        continue;
      }
      await addAnimationBundleToAdapter(bundle);
    }

    const missingAliases = (options.requiredAliases ?? []).filter(
      (alias) => !characterAnimationClipsContainAlias(loadedAnimationClips, alias),
    );
    if (missingAliases.length === 0) return;

    const message = `[smashparty] required character animation aliases missing${
      options.reason == null ? '' : ` for ${options.reason}`
    }: ${missingAliases.join(', ')}`;
    if (options.policy === 'required') throw new Error(message);
    warn(message);
  };

  return {
    adapter,
    eyeTextureStore,
    assetCounts,
    areAllCosmeticsLoaded,
    ensureAllCosmeticsLoaded,
    loadAnimationBundle,
    addAnimationBundleToAdapter,
    ensureAnimationBundles,
    getAnimationBundleStatus(bundle) {
      return bundleStatus.get(bundle) ?? 'not-requested';
    },
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
